import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { v4 as uuidv4 } from "uuid";
import Clarity from "@microsoft/clarity";

/**
 * Extracts the Clarity session ID from the _clck cookie.
 * Returns null if not available yet.
 */
export function getClaritySessionId(): string | null {
  try {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === "_clck") {
        return value.split("|")[0] || null;
      }
    }
    // Fallback: try Clarity's internal state
    const clarityState = (window as any)._clarity;
    if (clarityState && clarityState.v) {
      return clarityState.v;
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * Hook to initialize and track page views with Microsoft Clarity
 * Requires @microsoft/clarity package to be installed:
 * yarn add @microsoft/clarity
 *
 * Also requires uuid package for anonymous ID generation:
 * yarn add uuid
 */
const APP_SOURCE = "customer_frontend_v2";

const useClarityTracking = () => {
  const clarityProjectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
  const location = useLocation();
  const { userToken, account, userPermission } = useSelector((state: RootState) => state.authState);
  const initRef = useRef(false);

  // Get browser ID from localStorage or generate a new one
  // If forceNew is true, always generate a new ID regardless of localStorage value
  const getBrowserId = (forceNew = false) => {
    const storageKey = "haulpay_device_id";
    let deviceId = !forceNew ? localStorage.getItem(storageKey) : null;
    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem(storageKey, deviceId);
    }
    return deviceId;
  };

  // Determine if auth state is "ready" for Clarity initialization
  // Ready means: no token (anonymous user) OR token with account and permissions loaded
  const isAuthReady = !userToken || (userToken && account?.id && userPermission);

  // Initialize Clarity once auth state is ready
  useEffect(() => {
    if (!initRef.current && clarityProjectId && typeof window !== "undefined" && isAuthReady) {
      try {
        // Get browser ID - this is consistent regardless of login state
        const browserId = getBrowserId();

        // Use account ID as user ID if authenticated, else browserId
        const userId = userToken && account?.id ? account.id.toString() : browserId;

        // Use browserId as custom session ID for continuity
        const customSessionId = browserId;

        // Initialize first, then identify and set attributes
        Clarity.init(clarityProjectId);

        // Set optional configuration
        // Removed NODE_ENV check to allow uploads from localhost for testing
        // Clarity.consent(true); // Explicitly enable if needed, but init implies consent

        // Identify with user ID and session ID
        Clarity.identify(userId, customSessionId);

        // Set device identifier
        Clarity.setTag("deviceId", browserId);

        // Set authentication status and user info as tags
        Clarity.setTag("appSource", APP_SOURCE);

        if (userToken && account?.id) {
          Clarity.setTag("authenticated", "true");
          Clarity.setTag("authUserId", account.id.toString());
          if (account.email) Clarity.setTag("email", account.email);
          if (account.type) Clarity.setTag("accountType", account.type);
          const userTypes = userPermission?.user_type || [];
          if (userTypes.length > 0) Clarity.setTag("userTypes", [...userTypes].sort().join(","));
          if (userPermission?.has_factoring !== undefined)
            Clarity.setTag("hasFactoring", userPermission.has_factoring ? "true" : "false");
          if (userPermission?.dba) Clarity.setTag("dba", userPermission.dba);
          // Track authenticated initialization
          Clarity.event("initialized_authenticated");
        } else {
          Clarity.setTag("authenticated", "false");
          Clarity.setTag("authUserId", "anonymous");
          // Track anonymous initialization
          Clarity.event("initialized_anonymous");
        }

        initRef.current = true;
      } catch (error) {
        // console.error("Error initializing Clarity:", error);
      }
    }
  }, [clarityProjectId, isAuthReady, userToken, account, userPermission]); // Wait for auth to be ready before init

  // Handle authentication state changes after initial setup
  // Update user ID and tags when auth changes
  useEffect(() => {
    // Only run if Clarity has been initialized
    if (initRef.current && typeof window !== "undefined") {
      try {
        // Use browserId as custom session ID for continuity
        const customSessionId = getBrowserId();

        // Use account ID as user ID if authenticated, else browserId
        let userId = userToken && account?.id ? account.id.toString() : customSessionId;

        // Reinforce identification with updated user ID and session ID
        Clarity.identify(userId, customSessionId);

        // Update tags based on auth state
        if (userToken && account?.id) {
          // User logged in - Update tags with account info
          Clarity.setTag("authenticated", "true");
          Clarity.setTag("authUserId", account.id.toString());
          if (account.email) Clarity.setTag("email", account.email);
          if (account.type) Clarity.setTag("accountType", account.type);
          const userTypes = userPermission?.user_type || [];
          if (userTypes.length > 0) Clarity.setTag("userTypes", [...userTypes].sort().join(","));
          if (userPermission?.has_factoring !== undefined)
            Clarity.setTag("hasFactoring", userPermission.has_factoring ? "true" : "false");
          if (userPermission?.dba) Clarity.setTag("dba", userPermission.dba);

          // Track auth change event
          Clarity.event("user_authenticated");
        } else {
          // User logged out - Update tags as anonymous
          Clarity.setTag("authenticated", "false");
          Clarity.setTag("authUserId", "anonymous");
          Clarity.setTag("email", ""); // Clear if needed
          Clarity.setTag("accountType", "");

          // Track logout event
          Clarity.event("user_logged_out");

          // Generate a completely new browser ID on logout
          const newBrowserId = getBrowserId(true); // Force new ID generation
          userId = newBrowserId;

          // Re-identify with new IDs
          Clarity.identify(userId, newBrowserId);
          Clarity.setTag("deviceId", newBrowserId); // Update tag
        }
      } catch (error) {
        // console.error("Error updating Clarity after auth change:", error);
      }
    }
  }, [userToken, account, userPermission]);

  // Track page views on route change (no re-identify — already handled by init + auth effects)
  useEffect(() => {
    if (initRef.current) {
      try {
        // Log page view event
        Clarity.event(`pageView_${location.pathname}${location.search}`);

        // Upgrade important business paths
        const importantPaths = [
          { path: "/haulpay/request-pay", reason: "payment_request" },
          {
            path: "/haulpay/carrier/manage-pay/payment-details/",
            reason: "carrier_payment_details"
          },
          {
            path: "/haulpay/assigned-loads/payment-details/",
            reason: "assigned_load_payment_details"
          },
          { path: "/haulpay/broker/funding-request/", reason: "broker_funding_request_details" },
          { path: "/loads/post/create", reason: "load_creation" },
          { path: "/loads/post/create/funding-request", reason: "load_funding_request" },
          { path: "/haulpay/broker/carriers/add", reason: "invited_carrier_addition" },
          { path: "/invited/", reason: "invited_carrier_invitation" }
        ];

        for (const { path, reason } of importantPaths) {
          if (location.pathname.includes(path)) {
            Clarity.upgrade(reason);
            break;
          }
        }
      } catch (error) {
        // console.error("Error tracking page view in Clarity:", error);
      }
    }
  }, [location]); // Only re-run on route changes

  // Capture Clarity session ID after init (with delay for cookie to be set)
  const [claritySessionId, setClaritySessionId] = useState<string | null>(null);
  useEffect(() => {
    const interval = setInterval(() => {
      if (!initRef.current) return;
      const sessionId = getClaritySessionId();
      if (sessionId) {
        setClaritySessionId(sessionId);
        clearInterval(interval);
      }
    }, 300);
    return () => clearInterval(interval);
  }, []);

  const identifyAnonymousUser = (email: string, name?: string) => {
    try {
      Clarity.setTag("email", email);
      if (name) Clarity.setTag("name", name);
      Clarity.event("signup_step1_identified");
    } catch (error) {
      // console.error("Error identifying anonymous user in Clarity:", error);
    }
  };

  return { identifyAnonymousUser, claritySessionId };
};

export default useClarityTracking;
